<template>
  <div class="wl-panel wl-form">
    <div class="wl-panel__header">
      <span class="wl-panel__title"><component :is="titleIcon" /> {{ title }}</span>
      <span class="wl-panel__nav">
        <button type="button" title="上一项 (◀ / ▲)" @click="step(-1)"><LeftOutlined /></button>
        <b>{{ navLabel }}</b>
        <button type="button" title="下一项 (▶ / ▼)" @click="step(1)"><RightOutlined /></button>
      </span>
      <span class="wl-panel__tools">
        <button type="button" :title="activeAction ? '删除该动作' : '删除航点'" @click="removeCurrent"><DeleteOutlined /></button>
        <button type="button" title="收起面板" @click="editor.rightPanelOpen.value = false"><CloseOutlined /></button>
      </span>
    </div>

    <div class="wl-panel__body uranus-scrollbar">
      <template v-if="selected.length > 1">
        <p class="wl-panel__hint">批量编辑 {{ selected.length }} 个航点；修改会应用到全部已选航点。</p>
        <div class="wl-field">
          <label>航点高度 (m)</label>
          <div class="wl-field__row">
            <a-checkbox :checked="allSame('useGlobalHeight') && Boolean(selected[0].useGlobalHeight)" :indeterminate="!allSame('useGlobalHeight')" @change="editor.setWaypointField('useGlobalHeight', $event.target.checked)">跟随全局</a-checkbox>
            <a-input-number size="small" :disabled="allSame('useGlobalHeight') && Boolean(selected[0].useGlobalHeight)" :value="commonValue('height')" :min="heightMin" :max="1500" :precision="1" @change="setNumber('height', $event)" />
          </div>
        </div>
        <div class="wl-field">
          <label>航点速度 (m/s)</label>
          <div class="wl-field__row">
            <a-checkbox :checked="allSame('useGlobalSpeed') && Boolean(selected[0].useGlobalSpeed)" :indeterminate="!allSame('useGlobalSpeed')" @change="editor.setWaypointField('useGlobalSpeed', $event.target.checked)">跟随全局</a-checkbox>
            <a-input-number size="small" :disabled="allSame('useGlobalSpeed') && Boolean(selected[0].useGlobalSpeed)" :value="commonValue('speed')" :min="1" :max="maxSpeed" :precision="1" @change="setNumber('speed', $event)" />
          </div>
        </div>
        <div class="wl-field">
          <label>云台俯仰角 (°)</label>
          <a-input-number size="small" :value="commonValue('gimbalPitchAngle')" :min="pitchRange[0]" :max="pitchRange[1]" @change="setNumber('gimbalPitchAngle', $event)" />
        </div>
        <div class="wl-field">
          <label>添加动作</label>
          <a-dropdown :trigger="['click']" overlay-class-name="wl-menu">
            <a-button size="small" block>为每个已选航点追加动作 <DownOutlined /></a-button>
            <template #overlay><a-menu @click="editor.addAction($event.key)"><a-menu-item v-for="type in actionTypes" :key="type">{{ ACTION_LABELS[type] }}</a-menu-item></a-menu></template>
          </a-dropdown>
        </div>
      </template>

      <template v-else-if="activeAction">
        <WaypointActionEditor :action="activeAction" :lenses="lenses" :pitch-range="pitchRange" :zoom-range="zoomRange" @update="editor.updateAction(activeAction.id, $event)" />
        <div class="wl-panel__order">
          <a-button size="small" :disabled="editor.activeActionIndex.value === 0" @click="editor.moveAction(activeAction.id, -1)"><UpOutlined /> 上移</a-button>
          <a-button size="small" :disabled="editor.activeActionIndex.value >= waypoint.actions.length - 1" @click="editor.moveAction(activeAction.id, 1)"><DownOutlined /> 下移</a-button>
        </div>
      </template>

      <template v-else>
        <div class="wl-field">
          <label>航点高度 <small>{{ editor.heightMode.value === 'WGS84' ? '(ASL)' : '(ALT)' }}</small></label>
          <div class="wl-field__row">
            <a-checkbox :checked="waypoint.useGlobalHeight" @change="editor.setWaypointField('useGlobalHeight', $event.target.checked)">跟随全局</a-checkbox>
            <a-input-number size="small" :disabled="waypoint.useGlobalHeight" :value="waypointHeight(draft, waypoint)" :min="heightMin" :max="1500" :precision="1" @change="setNumber('height', $event)" />
            <span class="unit">m</span>
          </div>
        </div>
        <div class="wl-field">
          <label>航点速度</label>
          <div class="wl-field__row">
            <a-checkbox :checked="waypoint.useGlobalSpeed" @change="editor.setWaypointField('useGlobalSpeed', $event.target.checked)">跟随全局</a-checkbox>
            <a-input-number size="small" :disabled="waypoint.useGlobalSpeed" :value="waypoint.useGlobalSpeed ? draft.waypoint.autoFlightSpeed : waypoint.speed" :min="1" :max="maxSpeed" :precision="1" @change="setNumber('speed', $event)" />
            <span class="unit">m/s</span>
          </div>
        </div>
        <div class="wl-field">
          <label>飞行器偏航角</label>
          <a-select size="small" :value="waypoint.useGlobalHeadingParam ? 'global' : waypoint.headingParam.waypointHeadingMode" dropdown-class-name="wl-select" @change="setHeadingMode">
            <a-select-option value="global">跟随全局（{{ headingLabelOf(draft.waypoint.globalHeadingParam.waypointHeadingMode) }}）</a-select-option>
            <a-select-option v-for="option in HEADING_MODE_OPTIONS" :key="option.value" :value="option.value">{{ option.label }}</a-select-option>
          </a-select>
          <div v-if="!waypoint.useGlobalHeadingParam && waypoint.headingParam.waypointHeadingMode === 'smoothTransition'" class="wl-slider">
            <button type="button" @click="setHeadingAngle(waypoint.headingParam.waypointHeadingAngle - 1)"><MinusOutlined /></button>
            <a-slider :value="waypoint.headingParam.waypointHeadingAngle" :min="-180" :max="180" :tip-formatter="null" @change="setHeadingAngle" />
            <button type="button" @click="setHeadingAngle(waypoint.headingParam.waypointHeadingAngle + 1)"><PlusOutlined /></button>
            <b>{{ waypoint.headingParam.waypointHeadingAngle }}°</b>
          </div>
          <div v-if="!waypoint.useGlobalHeadingParam && waypoint.headingParam.waypointHeadingMode === 'towardPOI'" class="wl-field__row">
            <span class="value">{{ waypoint.headingParam.waypointPoiPoint ? `${waypoint.headingParam.waypointPoiPoint[1].toFixed(5)}, ${waypoint.headingParam.waypointPoiPoint[0].toFixed(5)}` : '未设置兴趣点' }}</span>
            <a-button size="small" @click="editor.pickPoi('selected')">在地图上设置</a-button>
          </div>
        </div>
        <div class="wl-field">
          <label>云台俯仰角 <small v-if="draft.waypoint.gimbalPitchMode !== 'usePointSetting'">（当前为手动控制模式，仅记录）</small></label>
          <div class="wl-slider">
            <button type="button" @click="setNumber('gimbalPitchAngle', waypoint.gimbalPitchAngle - 1)"><MinusOutlined /></button>
            <a-slider :value="waypoint.gimbalPitchAngle" :min="pitchRange[0]" :max="pitchRange[1]" :tip-formatter="null" @change="setNumber('gimbalPitchAngle', $event)" />
            <button type="button" @click="setNumber('gimbalPitchAngle', waypoint.gimbalPitchAngle + 1)"><PlusOutlined /></button>
            <b>{{ waypoint.gimbalPitchAngle }}°</b>
          </div>
        </div>
        <div class="wl-field">
          <label>航点类型</label>
          <a-select size="small" :value="waypoint.useGlobalTurnParam ? 'global' : waypoint.turnParam.waypointTurnMode" dropdown-class-name="wl-select" @change="setTurnMode">
            <a-select-option value="global">跟随全局</a-select-option>
            <a-select-option v-for="option in TURN_MODE_OPTIONS" :key="option.value" :value="option.value">{{ option.label }}</a-select-option>
          </a-select>
          <div v-if="showDamping" class="wl-field__row"><span>转弯截距 (m)</span><a-input-number size="small" :value="waypoint.turnParam.waypointTurnDampingDist" :min="0.2" :max="200" :precision="1" @change="editor.setWaypointNested('turnParam', 'waypointTurnDampingDist', $event)" /></div>
        </div>
        <div v-if="editor.droneProfile.value?.supportsRisky !== false" class="wl-field wl-field--inline">
          <label>禁止返航点 <a-tooltip title="上方有障碍物时启用，安全返航时先沿航线飞离该区间"><InfoCircleOutlined /></a-tooltip></label>
          <a-switch size="small" :checked="waypoint.isRisky" @change="editor.setWaypointField('isRisky', $event)" />
        </div>
        <div class="wl-field wl-field--inline">
          <label>经度 / 纬度</label>
          <a-input-number size="small" :value="waypoint.longitude" :min="-180" :max="180" :precision="7" :step="0.00001" :controls="false" @change="setCoordinate('longitude', $event)" />
          <a-input-number size="small" :value="waypoint.latitude" :min="-90" :max="90" :precision="7" :step="0.00001" :controls="false" @change="setCoordinate('latitude', $event)" />
        </div>

        <div class="wl-actions">
          <div class="wl-actions__head">
            <span>航点动作 ({{ waypoint.actions.length }})</span>
            <a-dropdown :trigger="['click']" overlay-class-name="wl-menu">
              <a-button size="small" type="primary"><PlusOutlined /> 添加动作</a-button>
              <template #overlay><a-menu @click="editor.addAction($event.key)"><a-menu-item v-for="type in actionTypes" :key="type">{{ ACTION_LABELS[type] }}</a-menu-item></a-menu></template>
            </a-dropdown>
          </div>
          <p v-if="!waypoint.actions.length" class="wl-panel__hint">到达航点后依次执行动作。在模拟飞行中按 F 添加定向拍照，或使用左侧“基于飞行器位置”按钮。</p>
          <div v-else class="wl-actions__list">
            <button v-for="(action, index) in waypoint.actions" :key="action.id" type="button" class="wl-actions__item" @click="editor.activeActionIndex.value = index">
              <span class="index">{{ index + 1 }}</span><span class="name">{{ describeActions([action]) }}</span><RightOutlined />
            </button>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import type { Component } from 'vue'
import {
  AimOutlined, CameraOutlined, CloseOutlined, ColumnWidthOutlined, CompassOutlined, DeleteOutlined, DownOutlined, EnvironmentOutlined, FieldTimeOutlined, FolderOutlined,
  GlobalOutlined, InfoCircleOutlined, LeftOutlined, MinusOutlined, PauseCircleOutlined, PictureOutlined, PlusOutlined, RightOutlined, RotateRightOutlined, StopOutlined,
  UpOutlined, VideoCameraAddOutlined, VideoCameraOutlined, ZoomInOutlined,
} from '@ant-design/icons-vue'
import type { DraftWaypoint, PayloadLens, WaypointActionType, WaypointHeadingMode } from '/@/types/wayline-editor'
import { ACTION_LABELS, ACTION_ORDER, HEADING_MODE_OPTIONS, TURN_MODE_OPTIONS, describeActions, waypointHeight } from '/@/utils/wayline-editor'
import { useWaylineEditor } from './editor-context'
import WaypointActionEditor from './WaypointActionEditor.vue'

const editor = useWaylineEditor()
const draft = editor.draft
const selected = computed(() => editor.selected.value)
const waypoint = computed(() => selected.value[0])
const waypointIndex = computed(() => draft.waypoint.waypoints.indexOf(waypoint.value))
const activeAction = computed(() => selected.value.length === 1 ? waypoint.value.actions[editor.activeActionIndex.value] || null : null)
const heightMin = computed(() => editor.heightMode.value === 'WGS84' ? -500 : 2)
const maxSpeed = computed(() => editor.droneProfile.value?.maxFlightSpeed || 15)
const pitchRange = computed<[number, number]>(() => editor.droneProfile.value?.gimbalPitchRange || [-90, 30])
const zoomRange = computed<[number, number]>(() => editor.payloadProfile.value?.zoomRange || [24, 24])
const lenses = computed<PayloadLens[]>(() => editor.payloadProfile.value?.lenses || ['wide'])
const actionTypes = computed(() => ACTION_ORDER.filter(type => {
  const profile = editor.droneProfile.value
  if (type === 'panoShot' && profile && !profile.supportsPano) return false
  if (type === 'orientedShoot' && profile && !profile.supportsOrientedShoot) return false
  if (type === 'zoom' && !editor.payloadProfile.value?.zoomRange) return false
  return true
}))
const ACTION_ICONS: Record<WaypointActionType, Component> = {
  takePhoto: CameraOutlined,
  startRecord: VideoCameraAddOutlined,
  stopRecord: VideoCameraOutlined,
  hover: PauseCircleOutlined,
  gimbalRotate: CompassOutlined,
  rotateYaw: RotateRightOutlined,
  zoom: ZoomInOutlined,
  focus: AimOutlined,
  customDirName: FolderOutlined,
  panoShot: GlobalOutlined,
  orientedShoot: PictureOutlined,
  startTimedShot: FieldTimeOutlined,
  startDistanceShot: ColumnWidthOutlined,
  endIntervalShot: StopOutlined,
}
const title = computed(() => selected.value.length > 1 ? `批量编辑 ${selected.value.length} 个航点` : activeAction.value ? ACTION_LABELS[activeAction.value.type] : `航点 ${waypointIndex.value + 1}`)
const titleIcon = computed<Component>(() => activeAction.value ? ACTION_ICONS[activeAction.value.type] : EnvironmentOutlined)
const navLabel = computed(() => selected.value.length > 1 ? `${selected.value.length} 项` : `${waypointIndex.value + 1}-${editor.activeActionIndex.value + 1}`)
const showDamping = computed(() => {
  if (!waypoint.value || waypoint.value.useGlobalTurnParam) return false
  const mode = waypoint.value.turnParam.waypointTurnMode
  return mode === 'coordinateTurn' || (mode === 'toPointAndPassWithContinuityCurvature' && waypoint.value.turnParam.useStraightLine)
})

function headingLabelOf (mode: WaypointHeadingMode) {
  return HEADING_MODE_OPTIONS.find(option => option.value === mode)?.label || mode
}

function step (direction: 1 | -1) {
  if (selected.value.length !== 1) { editor.selectByOffset(direction); return }
  const next = editor.activeActionIndex.value + direction
  if (next >= -1 && next < waypoint.value.actions.length) editor.activeActionIndex.value = next
  else editor.selectByOffset(direction)
}

function removeCurrent () {
  if (activeAction.value) editor.removeAction(activeAction.value.id)
  else editor.removeSelected()
}

function allSame (key: keyof DraftWaypoint) { return selected.value.every(item => item[key] === selected.value[0][key]) }
function commonValue (key: keyof DraftWaypoint) { return allSame(key) ? selected.value[0][key] as number : undefined }
function setNumber (key: keyof DraftWaypoint, value: number | null | undefined) {
  if (value === null || value === undefined || !Number.isFinite(value)) return
  editor.setWaypointField(key, value)
}
function setCoordinate (key: 'longitude' | 'latitude', value: number | null) {
  if (value === null) return
  editor.moveSelectedTo(key === 'longitude' ? value : waypoint.value.longitude, key === 'latitude' ? value : waypoint.value.latitude)
}
function setHeadingMode (mode: string) {
  if (mode === 'global') { editor.setWaypointField('useGlobalHeadingParam', true); return }
  editor.setWaypointNested('headingParam', 'waypointHeadingMode', mode)
}
function setHeadingAngle (value: number) {
  editor.setWaypointNested('headingParam', 'waypointHeadingAngle', Math.max(-180, Math.min(180, Math.round(value))))
}
function setTurnMode (mode: string) {
  if (mode === 'global') { editor.setWaypointField('useGlobalTurnParam', true); return }
  editor.setWaypointNested('turnParam', 'waypointTurnMode', mode)
}
</script>

<style lang="scss">
.wl-panel {
  display: flex; flex-direction: column; height: 100%; min-height: 0;
  &__header { display: grid; grid-template-columns: 1fr auto auto; align-items: center; gap: 8px; height: 40px; padding: 0 8px 0 12px; border-bottom: 1px solid var(--wl-border); }
  &__title { display: inline-flex; align-items: center; gap: 6px; color: #fff; font-size: 13px; font-weight: 600; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  &__nav { display: inline-flex; align-items: center; gap: 2px; }
  &__nav b { min-width: 34px; text-align: center; color: #fff; font-weight: 500; font-variant-numeric: tabular-nums; }
  &__nav button, &__tools button { width: 24px; height: 24px; border: 0; border-radius: 4px; background: transparent; color: #fff; cursor: pointer; }
  &__nav button:hover, &__tools button:hover { background: #ffffff1a; }
  &__tools { display: inline-flex; gap: 2px; }
  &__body { flex: 1; min-height: 0; overflow: auto; padding: 10px 12px; display: flex; flex-direction: column; gap: 12px; }
  &__hint { margin: 0; color: var(--wl-muted); line-height: 1.6; }
  &__order { display: flex; gap: 8px; }
}
.wl-field {
  display: flex; flex-direction: column; gap: 6px;
  label { color: #c5cbd2; display: flex; align-items: center; gap: 4px; }
  label small { color: var(--wl-muted); }
  &__row { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
  &__row .unit { color: var(--wl-muted); }
  &__row .value { color: #fff; }
  &--inline { flex-direction: row; align-items: center; flex-wrap: wrap; }
  &--inline label { min-width: 90px; }
  .ant-input-number { width: 110px; }
  .ant-select { width: 100%; }
}
.wl-slider {
  display: grid; grid-template-columns: 24px 1fr 24px 48px; align-items: center; gap: 6px;
  button { height: 24px; border: 0; border-radius: 3px; background: #35373c; color: #fff; cursor: pointer; }
  b { color: #fff; font-weight: 500; text-align: right; font-variant-numeric: tabular-nums; }
  .ant-slider { margin: 6px 4px; }
}
.wl-actions {
  border-top: 1px solid var(--wl-border); padding-top: 10px;
  &__head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 8px; color: #fff; }
  &__list { display: flex; flex-direction: column; gap: 4px; }
  &__item { display: flex; align-items: center; gap: 8px; height: 30px; padding: 0 8px; border: 0; border-radius: 4px; background: #24262a; color: #fff; cursor: pointer; text-align: left; }
  &__item:hover { background: #2c2d31; }
  &__item .index { width: 18px; height: 18px; border-radius: 50%; background: var(--wl-accent); font-size: 11px; text-align: center; line-height: 18px; flex-shrink: 0; }
  &__item .name { flex: 1; overflow: hidden; white-space: nowrap; text-overflow: ellipsis; }
}
</style>
